import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/http/apiClient";

const NEWS_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "airline", label: "Airline" },
  { value: "hotel", label: "Hotel" },
  { value: "destination", label: "Destination" },
  { value: "visa", label: "Visa" },
  { value: "regulatory", label: "Regulatory" },
  { value: "industry", label: "Industry" },
  { value: "technology", label: "Technology" },
  { value: "cruise", label: "Cruise" },
  { value: "deals", label: "Deals" },
];

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_ATTACHMENT_EXTS = ["pdf", "doc", "docx"];

interface StagedFile {
  file: File;
  preview?: string;
}

type Step = "idle" | "submitting" | "done" | "error";

export default function AddNewsPage() {
  const navigate = useNavigate();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [tags, setTags] = useState("");
  const [destinations, setDestinations] = useState("");
  const [source, setSource] = useState("");
  const [sourceAuthor, setSourceAuthor] = useState("");

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [externalImageUrl, setExternalImageUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const [galleryFiles, setGalleryFiles] = useState<StagedFile[]>([]);
  const [attachFiles, setAttachFiles] = useState<StagedFile[]>([]);

  const [step, setStep] = useState<Step>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  function splitCommaList(val: string): string[] {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  }

  function pickCoverFile(file: File) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setErrorMsg("Cover image must be JPEG, PNG, WebP, or GIF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { setErrorMsg("Cover image must be under 10 MB."); return; }
    setErrorMsg("");
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setExternalImageUrl("");
  }

  function onCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) pickCoverFile(file);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) pickCoverFile(file);
  }

  function clearCover() {
    setCoverFile(null);
    setCoverPreview(null);
    setExternalImageUrl("");
  }

  function onGalleryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const valid = files.filter((f) => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) setErrorMsg("Some images skipped (wrong type or > 10 MB).");
    setGalleryFiles((prev) => [...prev, ...valid.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))]);
    e.target.value = "";
  }

  function onAttachChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const valid = files.filter((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
      return ALLOWED_ATTACHMENT_EXTS.includes(ext) && f.size <= 50 * 1024 * 1024;
    });
    if (valid.length < files.length) setErrorMsg("Some files skipped (only PDF, DOC, DOCX up to 50 MB).");
    setAttachFiles((prev) => [...prev, ...valid.map((f) => ({ file: f }))]);
    e.target.value = "";
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");
    setStep("submitting");

    const fd = new FormData();
    if (title.trim()) fd.append("title", title.trim());
    if (summary.trim()) fd.append("summary", summary.trim());
    if (body.trim()) fd.append("body", body.trim());
    fd.append("category", category);
    if (tags.trim()) fd.append("tags", tags.trim());
    if (destinations.trim()) fd.append("destinations", destinations.trim());
    if (source.trim()) fd.append("source", source.trim());
    if (sourceUrl.trim()) fd.append("source_url", sourceUrl.trim());
    if (sourceAuthor.trim()) fd.append("source_author", sourceAuthor.trim());
    if (!coverFile && externalImageUrl.trim()) fd.append("cover_image_url", externalImageUrl.trim());
    if (coverFile) fd.append("cover_image", coverFile, coverFile.name);
    for (const { file } of galleryFiles) fd.append("gallery", file, file.name);
    for (const { file } of attachFiles) fd.append("attachments", file, file.name);

    try {
      await apiClient.post("/api/news/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStep("done");
    } catch (err: unknown) {
      setStep("error");
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err instanceof Error ? err.message : "Submission failed. Please try again.");
      setErrorMsg(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  function resetForm() {
    setStep("idle");
    setTitle(""); setSourceUrl(""); setSummary(""); setBody("");
    setCategory("general"); setTags(""); setDestinations("");
    setSource(""); setSourceAuthor(""); clearCover();
    setGalleryFiles([]); setAttachFiles([]);
    setErrorMsg("");
  }

  if (step === "done") {
    return (
      <div className="page-shell flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">News Published</h2>
          <p className="text-sm text-gray-500 mb-6">Your news article is live and visible in the feed.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={resetForm} className="px-5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">
              Add Another
            </button>
            <button onClick={() => navigate("/")} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
              Go to Feed
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isLoading = step === "submitting";

  return (
    <div className="page-shell">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" aria-label="Go back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Add News</h1>
            <p className="text-xs text-gray-400">All fields are optional — share what you have</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* News Link */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            News Link <span className="ml-1 text-xs font-normal text-gray-400">optional</span>
          </label>
          <input
            type="text"
            placeholder="https://example.com/article"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            maxLength={1000}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
          />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Title <span className="ml-1 text-xs font-normal text-gray-400">optional</span>
          </label>
          <input
            type="text"
            placeholder="e.g. New direct flights launched between Mumbai and Dubai"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
          />
          {title.length > 450 && (
            <p className={`mt-1 text-xs text-right ${title.length >= 500 ? "text-red-500" : "text-amber-500"}`}>{title.length}/500</p>
          )}
        </div>

        {/* Cover Image */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Cover Image <span className="ml-1 text-xs font-normal text-gray-400">optional</span>
          </label>
          {coverPreview ? (
            <div className="relative rounded-2xl overflow-hidden border border-gray-200">
              <img src={coverPreview} alt="Cover preview" className="w-full h-52 object-cover" />
              <button type="button" onClick={clearCover} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow hover:bg-white text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center h-40 rounded-2xl border-2 border-dashed transition-colors cursor-pointer ${
                dragOver ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50/40"
              }`}
            >
              <span className="text-3xl mb-2">🖼️</span>
              <p className="text-sm font-medium text-gray-700">Drag & drop cover image</p>
              <p className="text-xs text-gray-400 mt-1">or click to browse · JPEG, PNG, WebP, GIF · max 10 MB</p>
              <span className="mt-3 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium">Browse</span>
              <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onCoverChange} />
            </label>
          )}
          {!coverFile && (
            <div className="mt-2">
              <p className="text-xs text-gray-400 text-center mb-2">— or paste an image URL —</p>
              <input
                type="text"
                placeholder="https://example.com/image.jpg"
                value={externalImageUrl}
                onChange={(e) => setExternalImageUrl(e.target.value)}
                maxLength={1000}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
              />
            </div>
          )}
        </div>

        {/* Gallery Images */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Gallery Images <span className="ml-1 text-xs font-normal text-gray-400">optional · multiple</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {galleryFiles.map((sf, idx) => (
              <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200 group">
                <img src={sf.preview} alt={sf.file.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setGalleryFiles((p) => p.filter((_, i) => i !== idx))}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <label className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
              <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-xs text-gray-400">Add</span>
              <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={onGalleryChange} />
            </label>
          </div>
          <p className="text-xs text-gray-400">JPEG, PNG, WebP, GIF · max 10 MB each</p>
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Attachments <span className="ml-1 text-xs font-normal text-gray-400">optional · PDF, DOC, DOCX</span>
          </label>
          <div className="space-y-2">
            {attachFiles.map((sf, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <span className="text-xl">{sf.file.name.endsWith(".pdf") ? "📄" : "📝"}</span>
                <span className="text-sm text-gray-700 flex-1 truncate">{sf.file.name}</span>
                <span className="text-xs text-gray-400">{(sf.file.size / 1024 / 1024).toFixed(1)} MB</span>
                <button type="button" onClick={() => setAttachFiles((p) => p.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <label className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors w-fit">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <span className="text-sm text-gray-600">Attach PDF / DOCX</span>
              <input
                ref={attachInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                multiple
                className="hidden"
                onChange={onAttachChange}
              />
            </label>
          </div>
        </div>

        {/* Summary */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Summary <span className="ml-1 text-xs font-normal text-gray-400">optional · shown in list view</span>
          </label>
          <textarea
            rows={3}
            placeholder="Brief summary of the news article…"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={1000}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white resize-none"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Full Article Body <span className="ml-1 text-xs font-normal text-gray-400">optional · supports markdown</span>
          </label>
          <textarea
            rows={6}
            placeholder="Paste or write the full article content here…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={50000}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white resize-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white"
          >
            {NEWS_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Source & Author */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Source / Publication <span className="ml-1 text-xs font-normal text-gray-400">optional</span>
            </label>
            <input type="text" placeholder="e.g. Times of India" value={source} onChange={(e) => setSource(e.target.value)} maxLength={300}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">
              Author <span className="ml-1 text-xs font-normal text-gray-400">optional</span>
            </label>
            <input type="text" placeholder="e.g. Rahul Sharma" value={sourceAuthor} onChange={(e) => setSourceAuthor(e.target.value)} maxLength={300}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white" />
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Tags <span className="ml-1 text-xs font-normal text-gray-400">optional · comma-separated</span>
          </label>
          <input type="text" placeholder="e.g. flights, budget travel, monsoon" value={tags} onChange={(e) => setTags(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white" />
          {tags && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {splitCommaList(tags).map((t) => (
                <span key={t} className="px-2.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Destinations */}
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1">
            Destinations <span className="ml-1 text-xs font-normal text-gray-400">optional · comma-separated</span>
          </label>
          <input type="text" placeholder="e.g. Maldives, Dubai, Goa" value={destinations} onChange={(e) => setDestinations(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm bg-white" />
          {destinations && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {splitCommaList(destinations).map((d) => (
                <span key={d} className="px-2.5 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-200">{d}</span>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {errorMsg && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{errorMsg}</div>
        )}

        {/* Submit */}
        <div className="pt-2 pb-8">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Publishing…
              </>
            ) : (
              "Publish News"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
